"use client";

import { useState } from "react";

import BtnLoadingSvg from "@/components/atoms/btn-loading-svg/btn-loading-svg";
import LabelButtonWrapper from "@/components/atoms/button-wrapper/button-wrapper";
import RichText from "@/components/atoms/rich-text/rich-text";
import { ELIGIBLE, WITHDRAWAL_REQUESTED } from "@/config/app-constant";
import { useWithdrawal } from "@/hooks/withdrawal/use-withdrawal";

import WithdrawalConfirmationModal from "./withdrawal-confirmation-modal";
import WithdrawalSuccessModal from "./withdrawal-success-modal";

interface WithdrawalButtonProps {
  onWithdraw: (withdrawalReason: string) => void | Promise<void>;
  buttonText?: string;
  calculationNoteRichText?: string;
  processNoteRichText?: string;
  disabled?: boolean;
  loading?: boolean;
  policyNumber?: string;
  cardLastFourDigits?: string;
  modalTitle?: string;
  modalIntroText?: string;
  modalWarningText?: string;
  modalRefundInfoText?: string;
  modalOptionalText?: string;
  modalReasonPlaceholder?: string;
  confirmButtonText?: string;
  cancelButtonText?: string;
  successTitle?: string;
  successMessage?: string;
  successAdditionalInfo?: string;
  closeButtonText?: string;
}

export default function WithdrawalButton({
  onWithdraw,
  buttonText,
  calculationNoteRichText,
  processNoteRichText,
  disabled = false,
  loading = false,
  policyNumber,
  cardLastFourDigits,
  modalTitle,
  modalIntroText,
  modalWarningText,
  modalRefundInfoText,
  modalOptionalText,
  modalReasonPlaceholder,
  confirmButtonText,
  cancelButtonText,
  successTitle,
  successMessage,
  successAdditionalInfo,
  closeButtonText,
}: Readonly<WithdrawalButtonProps>) {
  const [isProcessing, setIsProcessing] = useState(false);
  const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);
  const [isSuccessModalOpen, setIsSuccessModalOpen] = useState(false);
  const { policyData } = useWithdrawal();

  const eligibilityStatus =
    policyData?.withdrawalData.withdrawalEligibilityStatus;

  const handleButtonClick = () => {
    setIsConfirmModalOpen(true);
  };

  const handleConfirmModalClose = () => {
    setIsConfirmModalOpen(false);
  };

  const handleSuccessModalClose = () => {
    setIsSuccessModalOpen(false);
  };

  const handleConfirm = async (withdrawalReason: string) => {
    setIsConfirmModalOpen(false);
    if (disabled || isProcessing || loading) return;

    setIsProcessing(true);
    try {
      await onWithdraw(withdrawalReason);
      setIsSuccessModalOpen(true);
    } finally {
      setIsProcessing(false);
    }
  };

  const isDisabled = disabled || isProcessing || loading;
  const isLoadingState = isProcessing || loading;

  return (
    <>
      <WithdrawalConfirmationModal
        isOpen={isConfirmModalOpen}
        onClose={handleConfirmModalClose}
        onConfirm={handleConfirm}
        policyNumber={policyNumber}
        cardLastFourDigits={cardLastFourDigits}
        modalTitle={modalTitle}
        modalIntroText={modalIntroText}
        modalWarningText={modalWarningText}
        modalRefundInfoText={modalRefundInfoText}
        modalOptionalText={modalOptionalText}
        modalReasonPlaceholder={modalReasonPlaceholder}
        confirmButtonText={confirmButtonText}
        cancelButtonText={cancelButtonText}
      />

      <WithdrawalSuccessModal
        isOpen={isSuccessModalOpen}
        onClose={handleSuccessModalClose}
        successTitle={successTitle}
        successMessage={successMessage}
        successAdditionalInfo={successAdditionalInfo}
        closeButtonText={closeButtonText}
      />

      <div className="space-y-spacing-l">
        {eligibilityStatus === "ELIGIBLE" && buttonText && (
          <LabelButtonWrapper
            id="withdrawal-page-submit-button"
            data-testid="withdrawal-button"
            isDisabled={isDisabled}
            label={buttonText}
            isLoading={isLoadingState}
            loadingIcon={isLoadingState ? <BtnLoadingSvg /> : <div />}
            hierarchy="primary"
            className="w-full !h-12 rounded-radius-l font-body-font text-base-font-size font-semibold z-[2] relative !leading-[18px]"
            type="button"
            onClick={handleButtonClick}
          />
        )}

        {(eligibilityStatus === WITHDRAWAL_REQUESTED ||
          eligibilityStatus === ELIGIBLE) &&
          (calculationNoteRichText || processNoteRichText) && (
            <div>
              {calculationNoteRichText && (
                <RichText
                  richText={calculationNoteRichText}
                  className="font-body-font text-4xs text-text-functional-subtle leading-4 text-center"
                  id="withdrawal-disclaimer-calculation"
                />
              )}

              {processNoteRichText && (
                <RichText
                  richText={processNoteRichText}
                  className="font-body-font text-4xs text-text-functional-subtle leading-4 text-center mt-spacing-s"
                  id="withdrawal-disclaimer-process"
                />
              )}
            </div>
          )}
      </div>
    </>
  );
}
